import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DeleteIcon } from "@/components/icons";
import { cn } from "@/lib/utils";

type PaymentZoneDrawingGuidelinePanelProps = {
  mode?: "create" | "edit";
  name: string;
  polygonCount?: number;
  isSaving: boolean;
  isAddingPolygons?: boolean;
  canDeletePolygon?: boolean;
  onDeletePolygon?: () => void;
  onCancel: () => void;
  onFinish: () => void;
};

export const PaymentZoneDrawingGuidelinePanel = ({
  mode = "create",
  name,
  polygonCount,
  isSaving,
  isAddingPolygons = false,
  canDeletePolygon = false,
  onDeletePolygon,
  onCancel,
  onFinish,
}: PaymentZoneDrawingGuidelinePanelProps) => {
  const isEditMode = mode === "edit";
  const badgeLabel = isEditMode && isAddingPolygons ? "Ajout" : isEditMode ? "Édition" : "Dessin";
  const guidelineText =
    isEditMode && isAddingPolygons
      ? "Cliquez sur la carte pour ajouter des polygones."
      : isEditMode
        ? polygonCount !== undefined && polygonCount > 0
          ? "Glissez un sommet pour le déplacer, cliquez-le pour le supprimer, ou cliquez un segment pour en ajouter un."
          : "Modifiez le nom, la couleur ou la géométrie de la zone."
        : "Cliquez sur la carte pour dessiner des polygones.";
  const showPolygonCount = polygonCount !== undefined && polygonCount > 0;
  const saveDisabled = isEditMode ? isSaving : isSaving || polygonCount === 0;

  return (
    <>
      <div
        className={cn(
          "absolute left-1/2 top-6 z-50 w-[370px] -translate-x-1/2 rounded-lg border border-border bg-card px-3 py-2 shadow-lg flex flex-col gap-1",
        )}
        style={{ pointerEvents: "none" }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Badge variant="outline">{badgeLabel}</Badge>
          <span className="text-sm font-semibold">{name || "Zone sans nom"}</span>
          {showPolygonCount && (
            <span className="text-xs text-muted-foreground font-medium ml-auto">
              {polygonCount} polygone{polygonCount > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">{guidelineText}</div>
      </div>

      <div
        className={cn("absolute left-1/2 bottom-6 z-50 flex gap-3 -translate-x-1/2")}
        style={{ pointerEvents: "auto" }}
      >
        {canDeletePolygon && onDeletePolygon && (
          <Button
            variant="outline"
            size="lg"
            onClick={onDeletePolygon}
            disabled={isSaving}
            className="gap-2 text-destructive hover:text-destructive"
          >
            <DeleteIcon className="h-4 w-4" />
            Supprimer ce polygone
          </Button>
        )}
        <Button variant="outline" size="lg" onClick={onCancel} disabled={isSaving}>
          × Annuler
        </Button>
        <Button size="lg" onClick={onFinish} disabled={saveDisabled}>
          {isSaving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>
    </>
  );
};
